/* eslint-disable @typescript-eslint/no-explicit-any */

import type {
  PipeContentData,
  Page,
  ComponentSchema,
  ComponentNode,
  ImagePresetKey,
  VideoFieldType,
} from '@api/types.gen'
import type { ImageFieldData } from '../composables/useImagePreset'

// ==========================================
// 1. EXTRACTOR INTERFACES
// ==========================================

export interface NodeExtractor<T> {
  /** Called on every single ComponentNode in the schema tree */
  process: (node: ComponentNode) => void
  /** Called at the very end to retrieve the collected data */
  getResult: () => T
}

// ==========================================
// 2. THE MAIN SCHEMA TREE WALKER
// ==========================================

export function walkSchema(
  payload: PipeContentData | Page | ComponentSchema | ComponentNode,
  extractors: NodeExtractor<any>[],
) {
  // The core recursive function that operates on actual nodes
  function walkNode(node: ComponentNode) {
    // 1. Let every attached extractor process this specific node
    extractors.forEach((extractor) => extractor.process(node))

    // 2. If the node has children, recursively walk down the tree
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(walkNode)
    }
  }

  // Intelligently route the payload based on your DTO structure
  if ('pages' in payload && Array.isArray(payload.pages)) {
    // It's PipeContentData: iterate through all pages
    payload.pages.forEach((page) => {
      if (page.content?.children && Array.isArray(page.content.children)) {
        page.content.children.forEach(walkNode)
      }
    })
    if (Array.isArray(payload.sharedNodes)) {
      payload.sharedNodes.forEach((sharedNode) => {
        if (sharedNode.content?.children && Array.isArray(sharedNode.content.children)) {
          sharedNode.content.children.forEach(walkNode)
        }
      })
    }
  } else if ('content' in payload && payload.content?.children) {
    // It's a single Page object
    payload.content.children.forEach(walkNode)
  } else if ('children' in payload && Array.isArray(payload.children) && !('nodeId' in payload)) {
    // It's a ComponentSchema object
    payload.children.forEach(walkNode)
  } else if ('nodeId' in payload) {
    // It's directly a ComponentNode
    walkNode(payload as ComponentNode)
  }
}

// ==========================================
// 3. THE IMAGE EXTRACTOR (WITH DEEP FIELD TRAVERSAL)
// ==========================================

export function createVideoExtractor(): NodeExtractor<VideoFieldType[]> {
  const extracted = new Map<string, VideoFieldType>()

  return {
    process: (node: ComponentNode) => {
      if (!node.fields) return

      function walkFieldsDeep(obj: any) {
        if (!obj || typeof obj !== 'object') return

        if (obj.type === 'video_field' && obj.id) {
          if (!extracted.has(obj.id)) {
            extracted.set(obj.id, {
              type: 'video_field',
              id: obj.id,
              label: obj.label,
            })
          }
          return
        }

        if (Array.isArray(obj)) {
          obj.forEach(walkFieldsDeep)
        } else {
          Object.values(obj).forEach(walkFieldsDeep)
        }
      }

      walkFieldsDeep(node.fields)
    },
    getResult: () => Array.from(extracted.values()),
  }
}

export function createSvgImageExtractor(): NodeExtractor<{ id: string }[]> {
  const extracted = new Map<string, { id: string }>()

  return {
    process: (node: ComponentNode) => {
      if (!node.fields) return

      function walkFieldsDeep(obj: any) {
        if (!obj || typeof obj !== 'object') return

        if (obj.type === 'image_field' && obj.id && obj.mimeType === 'image/svg+xml') {
          if (!extracted.has(obj.id)) extracted.set(obj.id, { id: obj.id })
          return
        }

        if (Array.isArray(obj)) {
          obj.forEach(walkFieldsDeep)
        } else {
          Object.values(obj).forEach(walkFieldsDeep)
        }
      }

      walkFieldsDeep(node.fields)
    },
    getResult: () => Array.from(extracted.values()),
  }
}

export function createImageExtractor(): NodeExtractor<ImageFieldData[]> {
  const extracted = new Map<string, ImageFieldData>()

  return {
    process: (node: ComponentNode) => {
      // If the node has no fields, skip it
      if (!node.fields) return

      // Recursive function to dig through nested objects/arrays looking for images
      function walkFieldsDeep(obj: any) {
        // Base case: Ignore primitives and nulls
        if (!obj || typeof obj !== 'object') return

        // 🎯 FOUND IT: If the object identifies itself as an image
        if (obj.type === 'image_field' && obj.id) {
          const presetName: ImagePresetKey = obj.presetName || 'large'
          const uniqueKey = `${obj.id}-${presetName}`

          if (!extracted.has(uniqueKey)) {
            extracted.set(uniqueKey, {
              id: obj.id,
              presetName: presetName,
              customSettings: obj.customSettings,
              type: 'image_field',
            })
          }
          // We found the image, no need to drill deeper into the image object itself
          return
        }

        // KEEP DIGGING: If it's an Array, check every item
        if (Array.isArray(obj)) {
          obj.forEach(walkFieldsDeep)
        }
        // KEEP DIGGING: If it's a standard Object, check every value
        else {
          Object.values(obj).forEach(walkFieldsDeep)
        }
      }

      // Kick off the deep dive into this node's fields
      walkFieldsDeep(node.fields)
    },
    getResult: () => Array.from(extracted.values()),
  }
}
